var _ = require("../../uam/module.js")(module.exports, {
  namespace: "egt.forge",
  title: "EGT Forge page",
  description: "EGT Forge creative & marketing agency micro-site (live; keyword-rich; anonymized work).",
  supportServer: true,
  fields: { reserveUrl: { title: "Reserve URL", type: "uri", description: "Reservation entry point.", required: false } }
});
var S = require("../helpers/egt.sections.js");
var mediaInventory = require("../helpers/egt.media-inventory.js");

/* The Forge CTA target: the project intake wizard at /forge/start. */
var START = "/forge/start";

/* ---- JSON-LD: ProfessionalService. The OfferCatalog + knowsAbout mirror the
 * Capability Matrix cells (S4) 1:1, so the full keyword-rich service surface is
 * legible to crawlers/AI even JS-off. Anonymized — no client/Review markup. */
function jsonLd() {
  var O = S.siteOrigin();
  var data = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "@id": O + "/forge#service",
    "url": O + "/forge",
    "provider": { "@id": O + "/#organization" },
    "areaServed": "Worldwide",
    "name": "EGT Forge",
    "description": "A creative and marketing agency: custom website design, headless and e-commerce builds, video and commercial production, event and live-stream production, brand and rebranding, paid social and marketing strategy, and nonprofit website and grant-application support for retail, small business, nonprofits, and public agencies.",
    "knowsAbout": [
      "Website design", "Custom websites", "Headless development", "Advanced development",
      "Headless commerce", "E-commerce", "Google Shopping feeds", "Website analysis",
      "Video production", "Commercial production", "Film production",
      "Event production", "Live-stream production",
      "Brand design", "Logo design", "Rebranding", "Identity systems",
      "Paid social", "Digital advertising", "Marketing strategy", "Audience research",
      "Nonprofit websites", "Grant application help"
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Capabilities",
      "itemListElement": [
        { "@type": "Service", "name": "Custom websites", "description": "Custom, fast, responsive website design and front-end builds." },
        { "@type": "Service", "name": "Headless commerce", "description": "Headless builds and advanced front-end architecture." },
        { "@type": "Service", "name": "E-commerce & Google Shopping", "description": "Online stores with clean product feeds for Google Shopping." },
        { "@type": "Service", "name": "Commercial & film production", "description": "Commercials, spots, and films — directed and edited in-house." },
        { "@type": "Service", "name": "Event & live-stream production", "description": "Interviews, live events, and multi-camera live-stream production." },
        { "@type": "Service", "name": "Brand & identity / logo design", "description": "Logos and identity systems that hold up under pressure." },
        { "@type": "Service", "name": "Rebranding", "description": "Full rebrands — strategy, mark, and rollout." },
        { "@type": "Service", "name": "Paid social & digital advertising", "description": "Paid-social campaigns and digital advertising that move numbers." },
        { "@type": "Service", "name": "Marketing strategy & research", "description": "Marketing strategy, audience research, and market research." },
        { "@type": "Service", "name": "Website analysis", "description": "Audits of speed, conversion, SEO, and accessibility on existing sites." },
        { "@type": "Service", "name": "Advanced development", "description": "Integrations, automation, and custom application development." },
        { "@type": "Service", "name": "Nonprofit sites & grant help", "description": "Websites, branding, and grant-application support for causes." }
      ].map(function (s, i) { return { "@type": "Offer", "position": i + 1, "itemOffered": s }; })
    }
  };
  return { n: "script", type: "application/ld+json", i: JSON.stringify(data) };
}

/* Per-surface JSON-LD: WebPage + BreadcrumbList for /forge. The
 * ProfessionalService entity (jsonLd() above) is the page's primary entity, so
 * the WebPage "about" points at it. Site-wide Organization + WebSite come from
 * S.page. URLs from S.siteOrigin(). */
function forgePageSchema() {
  var O = S.siteOrigin();
  return S.jsonLd([
    { "@type": "WebPage", "@id": O + "/forge#webpage", url: O + "/forge",
      name: "EGT Forge — Creative & Marketing Agency",
      isPartOf: { "@id": O + "/#website" },
      about: { "@id": O + "/forge#service" },
      breadcrumb: { "@id": O + "/forge#breadcrumb" } },
    { "@type": "BreadcrumbList", "@id": O + "/forge#breadcrumb",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: O + "/" },
        { "@type": "ListItem", position: 2, name: "Forge" }
      ] }
  ]);
}

/* ===================================================================
 * S1 — HERO: "The floor is hot." Poster-first looping craft video,
 * frosted glass card with the byline + a one-time gold underline draw.
 * =================================================================== */
function heroSection() {
  return {
    n: "section", c: "egt-section egt-canvas egt-canvas--tall egt-canvas--end egt-forge__hero",
    i: [
      S.media({ video: "/vis/buildthatstory.mp4",
        poster: mediaInventory.posterFor("/vis/buildthatstory.mp4") || "/vis/buildthatstory.jpg",
        /* intrinsic size of the default poster only — a media-manager captured
         * thumbnail may differ, so no size is claimed for it (SEO wave 2A-8) */
        width: mediaInventory.posterFor("/vis/buildthatstory.mp4") ? undefined : "1920",
        height: mediaInventory.posterFor("/vis/buildthatstory.mp4") ? undefined : "1080",
        alt: "", priority: true, parallax: 0.06 }),
      { n: "div", c: "egt__shell egt-canvas__inner",
        i: { n: "div", c: "egt-glass egt-glass--wide egt-forge__hero-card egt-reveal",
          i: [
            { n: "p", c: "egt__eyebrow", i: "EGT Forge — Creative & Marketing Agency" },
            { n: "h1", c: "egt-forge__hero-title",
              i: [
                "We ",
                { n: "span", c: "egt-forge__draw", "data-egt-draw": "1", i: "build" },
                " it, shoot it, brand it,", { n: "br" },
                " and run the ", { n: "span", c: "egt__gold", i: "campaign." }
              ] },
            { n: "p", c: "egt-forge__hero-lead",
              i: "Custom websites, commercial and video production, brand and rebranding, and full marketing campaigns — for retail, small business, nonprofits, and public agencies." },
            /* answer-first (SEO wave 2C-5): the compound name, what it is, and
             * the approved two-tier posture (agency clients on the platform now) */
            { n: "p", c: "egt-forge__hero-answer",
              i: "EGT Forge is the creative and marketing agency of EGT. We design and build the site, produce the video, shape the brand and run the campaign for you — and Forge clients work on the Universe Platform as part of the project, in its public beta." },
            { n: "div", c: "egt-forge__hero-actions",
              i: [
                S.cta(START, "Start a project"),
                { n: "a", href: "#forge-disciplines", c: "egt-cta egt-cta--text egt-forge__hero-skip", i: "See what we forge ↓" }
              ] }
          ] } }
    ]
  };
}

/* ===================================================================
 * S2 — THE DISCIPLINES: five "ingot" tiles that heat (desat -> full
 * color) on hover/focus/tap. Real DOM text for SEO/AI; one hot at a time.
 * =================================================================== */
var DISCIPLINES = [
  { id: "website", k: "01", t: "Website Design", img: "/imgs/we-know-websites.jpg",
    copy: "Custom, fast, responsive websites — built on our own platform, headless-capable.",
    tags: ["custom websites", "responsive", "headless-capable"] },
  { id: "production", k: "02", t: "Production", img: "/imgs/egt-forge-canvas.jpg",
    copy: "Commercials, spots, interviews, and live events. 20+ years directing and editing.",
    tags: ["commercial production", "video", "live events"] },
  { id: "brand", k: "03", t: "Brand & Rebranding", img: "/imgs/forge-brand.jpg",
    copy: "Logos and identity systems that hold up under pressure.",
    tags: ["logo design", "rebranding", "identity systems"] },
  { id: "campaign", k: "04", t: "Campaign Management", img: "/imgs/we-know-ads.jpg",
    copy: "Strategy, creative, paid social, and real audience research that moves numbers.",
    tags: ["paid social", "strategy", "audience research"] },
  { id: "nonprofit", k: "05", t: "Nonprofit Support", img: "/imgs/forge-nonprofit.jpg",
    copy: "Websites, branding, and grant-application help for religious and secular causes.",
    tags: ["nonprofit websites", "grant help", "community"] }
];

function disciplineTile(d) {
  return {
    n: "a", href: "#forge-matrix", c: "egt-forge__ingot", "data-egt-heat": d.id, id: "discipline-" + d.id,
    i: [
      { n: "span", c: "egt-forge__ingot-media", "aria-hidden": "true",
        i: { n: "img", c: "egt-forge__ingot-img", src: d.img, alt: "", loading: "lazy", decoding: "async" } },
      { n: "span", c: "egt-forge__ingot-line", "aria-hidden": "true" },
      { n: "span", c: "egt-forge__ingot-body",
        i: [
          { n: "span", c: "egt-forge__ingot-k", i: d.k },
          { n: "span", c: "egt-forge__ingot-t", i: d.t },
          { n: "span", c: "egt-forge__ingot-copy", i: d.copy },
          { n: "span", c: "egt-forge__ingot-tags", "aria-hidden": "true",
            i: d.tags.map(function (t) { return { n: "span", c: "egt-forge__tag", i: t }; }) }
        ] }
    ]
  };
}

function disciplinesSection() {
  return {
    n: "section", id: "forge-disciplines", c: "egt-section egt-forge__disc",
    i: { n: "div", c: "egt__shell",
      i: [
        { n: "div", c: "egt-section__head egt-reveal",
          i: [
            { n: "p", c: "egt__eyebrow", i: "What we forge" },
            { n: "h2", c: "egt-section__title", i: "Five things we forge." },
            { n: "p", c: "egt-section__lead",
              i: "Cold metal until you touch it. Hover or tap a tile to heat it — the work warms to full color." }
          ] },
        { n: "ul", c: "egt-forge__ingots egt-reveal egt-reveal--up", "data-egt-heatgroup": "1",
          i: DISCIPLINES.map(function (d, i) {
            return { n: "li", c: "egt-forge__ingot-wrap", style: "--egt-i:" + i, i: disciplineTile(d) };
          }) }
      ] }
  };
}

/* S3 — SHOWREEL removed: the "Watch the floor" click-to-play reel section was
 * deleted (founder request); its footage (/vis/buildthatstory.mp4) now plays
 * as the hero's looping background instead (see heroSection() above). */

/* ===================================================================
 * S4 — CAPABILITY MATRIX: a wall of capability cells (NOT clients) the
 * visitor operates themselves. Sector chips filter/dim the grid; every
 * cell is real DOM text (the keyword-rich Forge exception → max SEO/AI
 * legibility). Demonstrates RANGE; names NO clients, fakes no work history.
 *   data-cat  = capability axis (build / shoot / brand / campaign)
 *   data-sector = space-separated sectors a cell serves (for chip filter)
 * =================================================================== */
var SECTORS = [
  { id: "all", t: "All" },
  { id: "retail", t: "Retail" },
  { id: "small", t: "Small business" },
  { id: "nonprofit", t: "Nonprofit" },
  { id: "gov", t: "Government / public safety" }
];

var CAPABILITIES = [
  { cat: "build", t: "Custom websites",
    copy: "Fast, responsive, accessible websites — designed and built to perform.",
    sectors: ["retail", "small", "nonprofit", "gov"] },
  { cat: "build", t: "Headless commerce",
    copy: "Headless front-ends and advanced architecture for sites that need to scale.",
    sectors: ["retail", "small"] },
  { cat: "build", t: "E-commerce & Google Shopping",
    copy: "Online stores with clean product feeds wired for Google Shopping.",
    sectors: ["retail", "small"] },
  { cat: "build", t: "Advanced development",
    copy: "Integrations, automation, and custom application development.",
    sectors: ["small", "gov", "nonprofit"] },
  { cat: "build", t: "Website analysis",
    copy: "Audits of speed, conversion, SEO, and accessibility on an existing site.",
    sectors: ["retail", "small", "nonprofit", "gov"] },
  { cat: "shoot", t: "Commercial & film production",
    copy: "Commercials, spots, and films — directed and edited in-house.",
    sectors: ["retail", "small", "gov"] },
  { cat: "shoot", t: "Event & live-stream production",
    copy: "Interviews, live events, and multi-camera live-stream production.",
    sectors: ["nonprofit", "gov", "small"] },
  { cat: "brand", t: "Brand & identity / logo",
    copy: "Logos and identity systems that hold up under pressure.",
    sectors: ["retail", "small", "nonprofit", "gov"] },
  { cat: "brand", t: "Rebranding",
    copy: "Full rebrands — strategy, a new mark, and a clean rollout.",
    sectors: ["retail", "small", "nonprofit"] },
  { cat: "campaign", t: "Paid social & digital advertising",
    copy: "Paid-social campaigns and digital advertising that move numbers.",
    sectors: ["retail", "small", "gov"] },
  { cat: "campaign", t: "Marketing strategy & research",
    copy: "Marketing strategy, audience research, and market research.",
    sectors: ["retail", "small", "nonprofit", "gov"] },
  { cat: "build", t: "Nonprofit sites & grant help",
    copy: "Websites, branding, and grant-application support for causes.",
    sectors: ["nonprofit"] }
];

function capabilityCell(c) {
  return {
    n: "li", c: "egt-forge__cell egt-reveal egt-reveal--up",
    "data-cat": c.cat, "data-sector": c.sectors.join(" "),
    i: { n: "div", c: "egt-glass egt-forge__cell-card egt-lift",
      i: [
        { n: "span", c: "egt-forge__cell-cat", "aria-hidden": "true", i: c.cat },
        { n: "h3", c: "egt-forge__cell-t", i: c.t },
        { n: "p", c: "egt-forge__cell-copy", i: c.copy }
      ] }
  };
}

function sectorChip(s, first) {
  return {
    n: "button", type: "button", c: "egt-forge__chip", "data-sector-chip": s.id,
    "aria-pressed": first ? "true" : "false", i: s.t
  };
}

/* the one real result, re-homed as an anonymized capability OUTCOME (no
 * client, no numbered ledger). Keeps the initCount controller via data-egt-count. */
function matrixOutcome() {
  return {
    n: "div", c: "egt-forge__outcome egt-glass egt-glass--narrow egt-reveal",
    i: [
      { n: "p", c: "egt-forge__outcome-line",
        i: [
          "One outcome we can name: a nonprofit's donations more than ",
          { n: "span", c: "egt-forge__stat", "data-egt-count": "3", "data-egt-suffix": "×",
            /* render the FINAL value, so JS-off or never-revealed reads "3×",
             * never a literal "0×"; the count-up resets-to-0 then animates. */
            i: [ { n: "span", c: "egt-forge__stat-num", i: "3×" } ] },
          " ", { n: "span", c: "egt-forge__stat-word", i: "tripled" },
          " after a refreshed site and a smarter ask."
        ] }
    ]
  };
}

function matrixSection() {
  return {
    n: "section", id: "forge-matrix", c: "egt-section egt-forge__matrix",
    i: { n: "div", c: "egt__shell",
      i: [
        { n: "div", c: "egt-section__head egt-reveal",
          i: [
            { n: "p", c: "egt__eyebrow", i: "What we can build for you" },
            { n: "h2", c: "egt-section__title", i: "Pick a sector. See what we forge." },
            { n: "p", c: "egt-section__lead",
              i: "Not a list of clients — a wall of what we actually make. Tap a sector to see what we forge for it." }
          ] },
        { n: "div", c: "egt-forge__chips egt-reveal", role: "group",
          "aria-label": "Filter capabilities by sector", "data-matrix-chips": "1",
          i: SECTORS.map(function (s, i) { return sectorChip(s, i === 0); }) },
        /* polite live region: announces the active sector + visible count so
         * the filter has an audible effect for screen-reader users. Visually
         * hidden (.egt-sr-only); populated by initMatrix(). */
        { n: "p", c: "egt-forge__sr", "data-matrix-status": "1", "aria-live": "polite", role: "status" },
        { n: "ul", c: "egt-forge__cells egt-reveal egt-reveal--up", "data-matrix-cells": "1",
          i: CAPABILITIES.map(capabilityCell) },
        matrixOutcome()
      ] }
  };
}

/* ===================================================================
 * S5 — HOW WE WORK: the forge arc + a gold progress spine that fills
 * on scroll. "Get dirty. Get the details. Clean and perform."
 * =================================================================== */
var BEATS = [
  { k: "01", t: "Research", c: "Ride-alongs, audience and market research. We learn the floor before we light it." },
  { k: "02", t: "Design / Build", c: "Logos, identity, sites, headless front-ends. The craft of the thing gets made here." },
  { k: "03", t: "Produce / Shoot", c: "Commercials, films, interviews, live events — produced, directed, and edited in-house." },
  { k: "04", t: "Launch / Measure", c: "Ship it, run the campaign, watch the numbers, iterate. A finished thing that keeps working." }
];

function processSection() {
  return {
    n: "section", c: "egt-section egt-forge__proc", id: "forge-process", "data-egt-spine": "1",
    i: { n: "div", c: "egt__shell",
      i: [
        { n: "div", c: "egt-section__head egt-reveal",
          i: [
            { n: "p", c: "egt__eyebrow", i: "How we work" },
            { n: "h2", c: "egt-section__title",
              i: [ "Get dirty. Get the details. ", { n: "br" }, { n: "span", c: "egt__gold", i: "Clean and perform." } ] }
          ] },
        { n: "div", c: "egt-forge__beats",
          i: [
            { n: "span", c: "egt-forge__spine", "aria-hidden": "true",
              i: { n: "span", c: "egt-forge__spine-fill", "data-egt-spine-fill": "1" } }
          ].concat(BEATS.map(function (b, i) {
            return {
              n: "div", c: "egt-forge__beat egt-reveal egt-reveal--up", style: "--egt-i:" + i,
              i: [
                { n: "span", c: "egt-forge__beat-dot", "aria-hidden": "true" },
                { n: "span", c: "egt-forge__beat-k", i: b.k },
                { n: "h3", c: "egt-forge__beat-t", i: b.t },
                { n: "p", c: "egt-forge__beat-c", i: b.c }
              ]
            };
          })) }
      ] }
  };
}

/* ===================================================================
 * S6 — START A PROJECT: centered frosted CTA over the warm studio
 * register, with the deposit/no-checks policy framed as confidence.
 * =================================================================== */
function startSection() {
  return {
    n: "section", c: "egt-section egt-canvas egt-canvas--center egt-forge__start",
    i: [
      S.media({ img: "/imgs/egt-forge-canvas.jpg", alt: "", parallax: 0.1 }),
      { n: "div", c: "egt__shell egt-canvas__inner",
        i: { n: "div", c: "egt-glass egt-forge__start-card egt-reveal",
          i: [
            { n: "p", c: "egt__eyebrow", i: "Work with us" },
            { n: "h2", c: "egt-section__title", i: "Tell us what you're building." },
            { n: "p", c: "egt-forge__start-lead",
              i: "Bring the brief, the mess, the half-formed idea. We forge it into something that performs." },
            { n: "div", c: "egt-forge__start-actions", i: S.cta(START, "Start a project") },
            { n: "p", c: "egt-forge__start-policy",
              i: "New clients start with a deposit. No paper checks — just good work, on time." }
          ] } }
    ]
  };
}

exports.render = async function render() {
  /* Nav CTA is Forge's own primary action — "Start a Project" (START, the
   * intake wizard) — not "Join the Beta". No word-whoosh tease: that's
   * Platform-specific copy, gated to the unoverridden default CTA in nav()
   * (egt.sections.js). */
  return S.page("/reserve", [
    jsonLd(),
    forgePageSchema(),
    heroSection(),
    disciplinesSection(),
    matrixSection(),
    processSection(),
    startSection(),
    /* honesty footnote (required) — illustrative footage disclosure */
    { n: "section", c: "egt-section egt-forge__note",
      i: { n: "div", c: "egt__shell",
        i: { n: "p", c: "egt-forge__note-p",
          i: "Footage shown on this page is illustrative. Our work is anonymized — we show capability, not client names, logos, or links. “Built on our own platform” describes a capability behind our work, not a product we resell." } } }
  ], { navCta: { label: "Start a Project", href: START } });
};
